import { useMedia } from 'react-use';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  Banner,
  CloseButton,
  Flex,
  GridContent,
  Heading,
  Paragraph,
  Spacer,
  Text,
  TextButton,
  ThemeConstellation,
  useToast,
} from '@zillow/constellation';
import UnmaskedToast from '../UnmaskedToast';
import type { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import type { DateLocalizer, SlotInfo, ToolbarProps, View } from 'react-big-calendar';
import { Views } from 'react-big-calendar';
import type { LocalTime } from '@zg-rentals/ts-z-calendar-client';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';
import {
  type PerformTourActionWebApiResponseDto,
  type RemoveAvailabilityWebApiResponseDto,
  type SetAvailabilityWebApiResponseDto,
  type TourActionParams,
  TourActionType,
  type UpdateAvailabilityWebApiResponseDto,
} from '@zg-rentals/ts-z-calendar-client';
import { TZDate } from '@date-fns/tz';

import { StyledCalendar } from './StyledComponents';
import CalendarEvent from './components/CalendarEvent';
import DateHeader from './components/DateHeader';
import CalendarEventModal from './components/CalendarEventModal';
import CalendarEventPopover from './components/CalendarEvent/components/CalendarEventPopover';
import CalendarEventPopper from './components/CalendarEventPopper';
import TourCalendarToolbar from './components/TourCalendarToolbar';
import {
  createDateFnsLocalizer,
  formatTimeString,
  getAddToYourAvailabilityDates,
  isDatePastByTimeZone,
  isDateTooFarInAdvance,
  isUpdatedDateInPast,
  setTimeForDate,
} from '../../utils/formatTime';
import type {
  AvailabilityEvent,
  CalendarEventHandlers,
  OnCalendarEventSaveClickParams,
  TimeZoneIds,
  TourEvent,
  ZCalendarEvent,
} from '../../constants/TourCalendar';
import {
  AbbreviatedTimeZones,
  DefaultSelectedWeeklyRecurringDays,
  getTourCalendarDescription,
  TOUR_CALENDAR_HEADER,
  TOUR_CALENDAR_MODES,
  Z_CALENDAR_EVENT_TYPE,
} from '../../constants/TourCalendar';
import { eventStyleGetter, slotGroupPropGetter, slotPropGetter } from './rbcPropGetters';
import { getIsAvailabilityEvent, getIsEventDragAndDroppable } from '../../utils/calendarEventGetters';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';
import { CALENDAR_CLICKSTREAM_ACTION, emitCalendarEventActionEvent } from '../../constants/TouringClickStreamEvents';
import useTourCalendarSurveyCookie from '../../hooks/useTourCalendarSurveyCookie';
import {
  getAvailabilityEndTimeByTourDuration,
  getAvailabilityStartTimeByTourDuration,
  isDateBeforeOrEqual,
  isMultipleDayAvailability,
  isOverlappingCalendarAvailability,
  isStartAndEndTimeFullHourBlock,
} from '../../utils/availabilityTimeUtils';
import {
  getCalendarStepByTourDuration,
  getMaxCalendarEndTime,
  getMinCalendarStartTime,
  getMinEventDuration,
  getNumTimeSlotsPerGroupByTourDuration,
} from '../../utils/calendarUtils';
// @ts-ignore
import dayLayoutAlgorithm from '../../utils/dayLayoutAlgorithm';
import {
  END_TIME_BEFORE_START_TIME_ERROR_MESSAGE,
  EVENT_SPANS_MULTIPLE_DAYS_ERROR_MESSAGE,
  FULL_HOUR_BLOCK_ERROR_MESSAGE,
  OVERLAPPING_AVAILABILITY_ERROR_MESSAGE,
  PAST_DATE_ERROR_MESSAGE,
  TOO_FAR_IN_ADVANCE_ERROR_MESSAGE,
} from '../../constants/ErrorMessageConstants';

import TourCalendarContext from '../TourCalendarWithSettings/context/TourCalendarContext';

const { breakpoints } = ThemeConstellation.ns();

const StyledDnDCalendar = withDragAndDrop<ZCalendarEvent>(StyledCalendar);
const localizer = createDateFnsLocalizer();

type TourCalendarProps = {
  availabilityEvents: Array<AvailabilityEvent>;
  tourEvents: Array<TourEvent>;
  addAvailabilityEvent: (availabilityEvent: AvailabilityEvent) => Promise<SetAvailabilityWebApiResponseDto>;
  updateAvailabilityEvent: (availabilityEvent: AvailabilityEvent) => Promise<UpdateAvailabilityWebApiResponseDto>;
  removeAvailabilityEvent: (eventId: string) => Promise<RemoveAvailabilityWebApiResponseDto>;
  performTourAction: (
    tourEvent: TourEvent,
    actionType: TourActionType,
    tourActionParams?: TourActionParams,
  ) => Promise<PerformTourActionWebApiResponseDto>;
  mode: TOUR_CALENDAR_MODES;
  titleTag?: keyof JSX.IntrinsicElements;
  onClickSettings?: (shouldDisplaySettings: boolean) => void;
  timeZone: TimeZoneIds;
  clickStreamMetadata?: ClickStreamMetadata;
  listingAlias: string;
  isInMobileWebView: boolean;
  calendarStartTime: LocalTime;
  calendarEndTime: LocalTime;
  canEditTourDuration?: boolean;
  tourDuration?: TourDuration;
};

const TourCalendar = ({
  availabilityEvents,
  tourEvents,
  addAvailabilityEvent,
  updateAvailabilityEvent,
  removeAvailabilityEvent,
  performTourAction,
  mode,
  titleTag,
  onClickSettings,
  timeZone,
  clickStreamMetadata,
  listingAlias,
  isInMobileWebView,
  calendarStartTime,
  calendarEndTime,
  canEditTourDuration = false,
  tourDuration = TourDuration.MINUTES_30,
}: TourCalendarProps) => {
  const { canRescheduleTours } = useContext(TourCalendarContext);
  const [view, setView] = useState<View>(Views.WEEK);
  const [currentEvent, setCurrentEvent] = useState<ZCalendarEvent>();
  const [displayCalendarEventModal, setDisplayCalendarEventModal] = useState(false);
  // Availability events have two display modes: edit and preview.
  // displayEventEditPopover is used to determine if the edit mode should be displayed.
  const [displayEventEditPopover, setDisplayEventEditPopover] = useState(false);
  const [displayEventPreviewPopover, setDisplayEventPreviewPopover] = useState(false);
  const { hasDismissedTourCalendarSurvey, setTourCalendarSurveyCookie } = useTourCalendarSurveyCookie();
  const [currentTime, setCurrentTime] = useState(new Date());

  const allCalendarEvents = useMemo(() => [...availabilityEvents, ...tourEvents], [availabilityEvents, tourEvents]);

  const [minCalendarTime, maxCalendarTime] = useMemo(() => {
    const minValues = allCalendarEvents.map((event) => event.start.getHours());
    const maxValues = allCalendarEvents.map((event) =>
      event.end.getMinutes() > 0 ? event.end.getHours() + 1 : event.end.getHours(),
    );

    return [getMinCalendarStartTime(minValues, calendarStartTime), getMaxCalendarEndTime(maxValues, calendarEndTime)];
  }, [allCalendarEvents, calendarStartTime, calendarEndTime]);
  const minEventDuration = useMemo(() => {
    return getMinEventDuration(allCalendarEvents, tourDuration);
  }, [allCalendarEvents, tourDuration]);

  const isScreenLarge = useMedia(`(min-width: ${breakpoints.lg}px)`);
  const { enqueueToast } = useToast();

  const isInTourAvailabilityMode = mode === TOUR_CALENDAR_MODES.TOUR_AVAILABILITY;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    if (isInTourAvailabilityMode && availabilityEvents?.length === 0) {
      displayAddToYourAvailabilityModal();
    }
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isScreenLarge) {
      setView(Views.WEEK);
    } else {
      setView(Views.DAY);
    }
  }, [isScreenLarge]);

  const handleCalendarActionClickStreamEvent = useCallback(
    (calendarActionType: CALENDAR_CLICKSTREAM_ACTION) => {
      if (clickStreamMetadata) {
        emitCalendarEventActionEvent(clickStreamMetadata, calendarActionType);
      }
    },
    [clickStreamMetadata],
  );

  const displayAddToYourAvailabilityModal = () => {
    const { start, end } = getAddToYourAvailabilityDates(tourDuration);
    setCurrentEvent({
      start,
      end,
      // id doesn't matter here because this event will be overwritten by the backend response
      id: '',
      isRecurringEvent: false,
      recurringDays: DefaultSelectedWeeklyRecurringDays,
      recurrenceEndDateMs: 0,
      eventType: Z_CALENDAR_EVENT_TYPE.IN_PROGRESS_AVAILABILITY,
      isPastEvent: false,
    });
    setDisplayCalendarEventModal(true);
    setDisplayEventEditPopover(true);
  };

  const onAddToYourAvailabilityClick = () => {
    displayAddToYourAvailabilityModal();
    handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.ADD_TOUR_EVENT);
  };

  const onSelectSlot = (event: SlotInfo) => {
    if (displayEventEditPopover || displayEventPreviewPopover) {
      return;
    }

    if (isDatePastByTimeZone(event.start, timeZone)) {
      enqueueToast(<UnmaskedToast appearance="error" body={PAST_DATE_ERROR_MESSAGE} />);
      return;
    }

    if (isDateTooFarInAdvance(event.start, timeZone)) {
      enqueueToast(<UnmaskedToast appearance="error" body={TOO_FAR_IN_ADVANCE_ERROR_MESSAGE} />);
      return;
    }

    const updatedStart = getAvailabilityStartTimeByTourDuration(event.start, tourDuration, minEventDuration);
    const updatedEnd = getAvailabilityEndTimeByTourDuration(updatedStart, event.end, tourDuration, minEventDuration);

    setCurrentEvent({
      start: updatedStart,
      end: updatedEnd,
      // id doesn't matter here because this event will be overwritten by the backend response
      id: '',
      isRecurringEvent: false,
      recurringDays: DefaultSelectedWeeklyRecurringDays,
      recurrenceEndDateMs: 0,
      eventType: Z_CALENDAR_EVENT_TYPE.IN_PROGRESS_AVAILABILITY,
      isPastEvent: false,
    });
    setDisplayCalendarEventModal(true);
    setDisplayEventEditPopover(true);
    handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.ADD_TOUR_MOUSE_CLICK);
  };

  const onSelectEvent = (event: ZCalendarEvent) => {
    // Needed because event popover is considered part of the calendar event since it is rendered in CalendarEvent
    // This causes issues where this onSelectEvent will block interactions with the event popover
    // Preventing onSelectEvent on the selected event fixes this issue
    const isEventPopoverVisible = displayEventEditPopover || displayEventPreviewPopover;
    if (isEventPopoverVisible && currentEvent?.id === event.id) {
      return;
    }

    setCurrentEvent(event);
    if (getIsAvailabilityEvent(event) || canRescheduleTours) {
      setDisplayCalendarEventModal(true);
      setDisplayEventPreviewPopover(true);
    } else {
      setDisplayEventPreviewPopover(true);
    }
  };

  const dragAndDropHandler = useCallback(
    ({ event: existingEvent, start, end }: EventInteractionArgs<ZCalendarEvent>) => {
      const updatedAvailabilityEvent = { ...existingEvent, start: new Date(start), end: new Date(end) };
      const isStartDateInPast = isUpdatedDateInPast({
        updatedDate: updatedAvailabilityEvent.start,
        existingDate: existingEvent.start,
        timeZone,
      });
      const isEndDateInPast = isUpdatedDateInPast({
        updatedDate: updatedAvailabilityEvent.end,
        existingDate: existingEvent.end,
        timeZone,
      });

      if (isStartDateInPast || isEndDateInPast) {
        enqueueToast(<UnmaskedToast appearance="error" body={PAST_DATE_ERROR_MESSAGE} />);
        return;
      }

      if (isDateTooFarInAdvance(updatedAvailabilityEvent.start, timeZone)) {
        enqueueToast(<UnmaskedToast appearance="error" body={TOO_FAR_IN_ADVANCE_ERROR_MESSAGE} />);
        return;
      }

      if (isDateBeforeOrEqual(updatedAvailabilityEvent.end, updatedAvailabilityEvent.start)) {
        enqueueToast(<UnmaskedToast appearance="error" body={END_TIME_BEFORE_START_TIME_ERROR_MESSAGE} />);
        return;
      }

      if (isMultipleDayAvailability(updatedAvailabilityEvent.start, updatedAvailabilityEvent.end)) {
        enqueueToast(<UnmaskedToast appearance="error" body={EVENT_SPANS_MULTIPLE_DAYS_ERROR_MESSAGE} />);
        return;
      }

      if (
        tourDuration === TourDuration.MINUTES_60 &&
        !isStartAndEndTimeFullHourBlock(updatedAvailabilityEvent.start, updatedAvailabilityEvent.end, tourDuration)
      ) {
        enqueueToast(<UnmaskedToast appearance="error" body={FULL_HOUR_BLOCK_ERROR_MESSAGE} />);
        return;
      }

      if (
        isOverlappingCalendarAvailability(
          updatedAvailabilityEvent.start,
          updatedAvailabilityEvent.end,
          availabilityEvents,
          existingEvent,
        )
      ) {
        enqueueToast(<UnmaskedToast appearance="error" body={OVERLAPPING_AVAILABILITY_ERROR_MESSAGE} />);
        return;
      }

      // event here must be an AvailabilityEvent because draggableAccessor and resizableAccessor only allow AvailabilityEvents to be dragged and resized
      updateAvailabilityEvent(updatedAvailabilityEvent as AvailabilityEvent);
    },
    [updateAvailabilityEvent, availabilityEvents, timeZone, enqueueToast, tourDuration],
  );

  const onCalendarEventClose = useCallback(() => {
    setCurrentEvent(undefined);
    setDisplayEventPreviewPopover(false);
    setDisplayEventEditPopover(false);
    setDisplayCalendarEventModal(false);
  }, []);

  const onCalendarEventSaveClick = useCallback(
    ({
      selectedEventDate,
      selectedStartTime,
      selectedEndTime,
      selectedFrequency,
      selectedWeeklyRecurringDays,
      selectedRepeatUntilDate,
    }: OnCalendarEventSaveClickParams) => {
      if (!currentEvent) {
        return;
      }

      const startDate = setTimeForDate(new Date(selectedEventDate), selectedStartTime);
      const endDate = setTimeForDate(new Date(selectedEventDate), selectedEndTime);
      const isStartDateInPast = isUpdatedDateInPast({
        updatedDate: startDate,
        existingDate: currentEvent.start,
        timeZone,
      });
      const isEndDateInPast = isUpdatedDateInPast({
        updatedDate: endDate,
        existingDate: currentEvent.end,
        timeZone,
      });

      if (isStartDateInPast || isEndDateInPast) {
        enqueueToast(<UnmaskedToast appearance="error" body={PAST_DATE_ERROR_MESSAGE} />);
        return;
      }

      if (isDateTooFarInAdvance(startDate, timeZone)) {
        enqueueToast(<UnmaskedToast appearance="error" body={TOO_FAR_IN_ADVANCE_ERROR_MESSAGE} />);
        return;
      }

      if (isOverlappingCalendarAvailability(startDate, endDate, availabilityEvents, currentEvent)) {
        enqueueToast(<UnmaskedToast appearance="error" body={OVERLAPPING_AVAILABILITY_ERROR_MESSAGE} />);
        return;
      }

      if (currentEvent.eventType !== Z_CALENDAR_EVENT_TYPE.IN_PROGRESS_AVAILABILITY) {
        updateAvailabilityEvent({
          ...(currentEvent as AvailabilityEvent),
          start: startDate,
          end: endDate,
          isRecurringEvent: Boolean(selectedFrequency),
          frequency: selectedFrequency,
          recurringDays: selectedWeeklyRecurringDays,
          recurrenceEndDateMs: setTimeForDate(new Date(selectedRepeatUntilDate), selectedEndTime).getTime(),
        });
        onCalendarEventClose();
      } else {
        addAvailabilityEvent({
          title: 'Available',
          start: startDate,
          end: endDate,
          // id doesn't matter here because this event will be overwritten by the backend response
          id: '',
          eventType: Z_CALENDAR_EVENT_TYPE.AVAILABILITY,
          isRecurringEvent: Boolean(selectedFrequency),
          frequency: selectedFrequency,
          recurringDays: selectedWeeklyRecurringDays,
          recurrenceEndDateMs: setTimeForDate(new Date(selectedRepeatUntilDate), selectedEndTime).getTime(),
          isPastEvent: false,
        });
        handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.SAVE_TOUR_EVENT);
        onCalendarEventClose();
      }
    },
    [
      currentEvent,
      addAvailabilityEvent,
      updateAvailabilityEvent,
      onCalendarEventClose,
      handleCalendarActionClickStreamEvent,
      enqueueToast,
      timeZone,
      availabilityEvents,
    ],
  );

  const calendarEventHandlers: CalendarEventHandlers = useMemo(
    () => ({
      onClose: onCalendarEventClose,
      onEditClick: () => {
        handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.EDIT_TOUR);
        setDisplayEventPreviewPopover(false);
        setDisplayEventEditPopover(true);
      },
      onDeleteClick: () => {
        if (!currentEvent) {
          return () => {};
        }

        return removeAvailabilityEvent(currentEvent.id).then(() => {
          handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.DELETE_TOUR);
          return onCalendarEventClose();
        });
      },
      onSaveClick: onCalendarEventSaveClick,
      onDeclineClick: () => {
        return performTourAction(currentEvent as TourEvent, TourActionType.IRENE_DECLINE).then(() => {
          handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.DECLINE_TOUR);
          return onCalendarEventClose();
        });
      },
      onConfirmClick: () => {
        return performTourAction(currentEvent as TourEvent, TourActionType.IRENE_CONFIRM).then(() => {
          handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.CONFIRM_TOUR);
          return onCalendarEventClose();
        });
      },
      onCancelTourConfirmationClick: () => {
        return performTourAction(currentEvent as TourEvent, TourActionType.IRENE_CANCEL, {
          ireneCancelReason: 'canceled',
        }).then(() => {
          handleCalendarActionClickStreamEvent(CALENDAR_CLICKSTREAM_ACTION.CANCEL_TOUR);
          return onCalendarEventClose();
        });
      },
    }),
    [
      currentEvent,
      onCalendarEventClose,
      onCalendarEventSaveClick,
      removeAvailabilityEvent,
      performTourAction,
      handleCalendarActionClickStreamEvent,
    ],
  );

  const now = useMemo(() => new TZDate(currentTime, timeZone), [currentTime, timeZone]);

  return (
    <>
      <GridContent
        gridColumnStart={{ default: '1', lg: '4' }}
        gridColumnEnd={{ default: 'span 24', lg: 'span 18' }}
        padding={0}
        data-testid="TourCalendar-Body"
      >
        <div>
          {isScreenLarge && isInTourAvailabilityMode ? (
            <Spacer marginBottom="lg" data-testid="TourCalendar-Desktop-Header-Description">
              <Heading level="4" as={titleTag ?? 'h4'} fontFamily="serif" marginBottom="xs">
                {TOUR_CALENDAR_HEADER}
              </Heading>
              <Paragraph fontColor="textMedium">{getTourCalendarDescription(true)}</Paragraph>
            </Spacer>
          ) : (
            !hasDismissedTourCalendarSurvey &&
            !isInTourAvailabilityMode && (
              <Banner
                body={
                  <Paragraph>
                    <Text paddingRight="xs">
                      Thanks for using our new tour booking feature! We&apos;d love your feedback on how it&apos;s
                      going.
                    </Text>
                    <TextButton as="a" href="https://forms.gle/LxP49GVNEeQw3NMt7" target="_blank">
                      Share your feedback
                    </TextButton>
                  </Paragraph>
                }
                closeButton={<CloseButton onClick={setTourCalendarSurveyCookie} />}
              />
            )
          )}
          <StyledDnDCalendar
            isInTourAvailabilityMode={isInTourAvailabilityMode}
            components={{
              event: CalendarEvent({
                canRescheduleTours: canRescheduleTours || false,
                selectedEvent: currentEvent,
                displayEventEditPopover,
                handlers: calendarEventHandlers,
                tourDuration,
              }),
              toolbar: (props: ToolbarProps<ZCalendarEvent>) => (
                <TourCalendarToolbar
                  mode={mode}
                  onClickSettings={onClickSettings}
                  onAddToYourAvailabilityClick={onAddToYourAvailabilityClick}
                  listingAlias={listingAlias}
                  isInMobileWebView={isInMobileWebView}
                  now={now}
                  {...props}
                />
              ),
              timeGutterHeader: () => {
                if (view === Views.WEEK) {
                  return (
                    <Paragraph
                      fontType="finePrint"
                      fontColor="textMedium"
                      // @ts-ignore - Paragraph doesn't seem to recognize textAlign
                      textAlign="center"
                      marginY="md"
                      data-testid="TourCalendar-Time-Zone"
                    >
                      {AbbreviatedTimeZones[timeZone]}
                    </Paragraph>
                  );
                }
                return null;
              },
              week: {
                header: DateHeader,
              },
              // @ts-expect-error
              timeIndicatorWrapper: (timeIndicatorWrapperProps) => {
                return (
                  <div {...timeIndicatorWrapperProps} style={timeIndicatorWrapperProps?.style}>
                    <Flex display="flex" alignItems="center" justifyContent="center" className="time-badge">
                      {formatTimeString(now, { showMeridiem: true })}
                    </Flex>
                  </div>
                );
              },
            }}
            view={view}
            views={['day', 'week']}
            // empty fn passed to remove error in tests
            onView={() => {}}
            events={allCalendarEvents}
            formats={{
              eventTimeRangeFormat: () => '',

              timeGutterFormat: (date: Date, culture?: string, dateLocalizer?: DateLocalizer) =>
                formatTimeString(date, { showMeridiem: true, truncateZeroMinutes: true }),
            }}
            localizer={localizer}
            selectable={true}
            onSelectSlot={onSelectSlot}
            onSelectEvent={onSelectEvent}
            selected={currentEvent}
            // draggableAccessor and resizableAccessor used to determine if event is draggable or resizable
            draggableAccessor={getIsEventDragAndDroppable}
            resizableAccessor={getIsEventDragAndDroppable}
            onEventDrop={dragAndDropHandler}
            onEventResize={dragAndDropHandler}
            eventPropGetter={eventStyleGetter}
            scrollToTime={now}
            getNow={() => now}
            dayLayoutAlgorithm={dayLayoutAlgorithm}
            slotPropGetter={slotPropGetter(now, timeZone)}
            slotGroupPropGetter={slotGroupPropGetter(minEventDuration)}
            showMultiDayTimes={true}
            // getDrilldownView and drilldownView are set to null to remove pointer cursor when hovering over date headers
            getDrilldownView={null}
            drilldownView={null}
            min={minCalendarTime}
            max={maxCalendarTime}
            step={getCalendarStepByTourDuration(minEventDuration)}
            timeslots={getNumTimeSlotsPerGroupByTourDuration(minEventDuration)}
          />
        </div>
      </GridContent>
      {displayCalendarEventModal && currentEvent && !canRescheduleTours && (
        <CalendarEventPopper
          event={currentEvent}
          popoverBody={
            <CalendarEventPopover
              event={currentEvent}
              displayEventEditPopover={displayEventEditPopover}
              handlers={calendarEventHandlers}
              canEditTourDuration={canEditTourDuration}
              tourDuration={tourDuration}
            />
          }
          onClose={onCalendarEventClose}
        />
      )}
      {displayCalendarEventModal && currentEvent && canRescheduleTours && (
        <CalendarEventModal
          canEditTourDuration={canEditTourDuration}
          displayEventEditPopover={displayEventEditPopover}
          event={currentEvent}
          handlers={calendarEventHandlers}
          tourDuration={tourDuration}
        />
      )}
    </>
  );
};

export default TourCalendar;
